/**
 * Imported plans: convert traced regions into the unit records the 3D scene and
 * the detail panel already consume, and persist them.
 *
 * The Mall Map's demo estate and an imported plan must be interchangeable, so
 * the output here matches the shape produced by data/mallData.js — same fields,
 * same derived commercial figures. The ONLY difference is where the footprint
 * came from: traced from a drawing rather than laid out along a strip.
 */

import { toMallSpace, footprint, centroid, area } from './geometry';
import { kindOf, isLeasable, isDrawn } from './regionKinds';
import {
    CATEGORY_COLORS, buildCommercials, buildMallStats,
} from '../data/mallData';

const STORAGE_KEY = 'mm_imported_plan_v1';

/** localStorage caps out around 5 MB; a rasterised plan blows straight past it. */
const MAX_STORED_PREVIEW_BYTES = 1_400_000;

export const DEFAULT_CATEGORY = 'Services';

/**
 * Turn reviewed regions into units for one floor.
 *
 * @param {object} floor    { id, name, short, y, level }
 * @param {Array}  regions  reviewed regions: { id, polygon (0..1), name, code, category, status }
 * @param {object} scale    { widthM, heightM } real-world size of the plan sheet
 */
/**
 * Split classified regions into the two things the map needs.
 *
 * UNITS are leasable and carry the whole commercial model — lease, sales,
 * ranking, a place in the dashboard. SURFACES are everything else the plan
 * draws: promenades, atria, cores, back-of-house, the apron outside. They are
 * geometry only, and they exist so the floor plate reads as a building rather
 * than as a scatter of shops floating on an empty slab.
 *
 * Keeping them apart is what lets the model be complete without the numbers
 * becoming nonsense: an atrium void has an area but it does not have a rent.
 */
export function regionsToRecords(floor, regions, scale) {
    const drawn = regions.filter((r) => isDrawn(r.kind));
    const units = regionsToUnits(floor, drawn.filter((r) => isLeasable(r.kind)), scale);
    const surfaces = drawn
        .filter((r) => !isLeasable(r.kind))
        .map((region, idx) => {
            const polyM = toMallSpace(region.polygon, scale.widthM, scale.heightM);
            const [cx, cz] = centroid(polyM);
            const kind = kindOf(region.kind);
            return {
                id: `${floor.id}-srf-${idx}`,
                kind: region.kind,
                name: region.name || kind.label,
                polygon: polyM,
                geom: footprint(polyM),
                center: { x: cx, z: cz },
                areaM2: Math.max(1, Math.round(area(polyM))),
                height: kind.height,
                planColor: region.color || null,
            };
        });
    return { units, surfaces };
}

export function regionsToUnits(floor, regions, scale) {
    return regions.map((region, idx) => {
        const polyM = toMallSpace(region.polygon, scale.widthM, scale.heightM);
        const fp = footprint(polyM);
        const [cx, cz] = centroid(polyM);

        // True polygon area, not the bounding box — an angled shopfront's box
        // can overstate its floor area by half.
        const gla = Math.max(1, Math.round(area(polyM)));

        const code = region.code || `${floor.id}-${String(idx + 1).padStart(3, '0')}`;
        const category = region.category || DEFAULT_CATEGORY;
        const status = region.status || 'Occupied';

        return {
            id: `${floor.id}-imp-${idx}`,
            code,
            name: status === 'Vacant' ? `Vacant Unit ${code}` : (region.name || code),
            brand: status === 'Vacant' ? null : (region.name || null),
            category,
            floor: floor.id,
            floorName: floor.name,
            floorY: floor.y,
            status,
            isAnchor: gla >= 1500,
            stripId: 'imported',
            polygon: polyM,
            geom: fp,
            center: { x: cx, z: cz },
            // Provisional door. buildPlanRouter replaces this with the point
            // where the outline actually meets the traced circulation, so the
            // shopfront marker and the route agree on where you walk in.
            frontage: { x: cx, z: cz },
            corridor: null,
            gla,
            /*
             * The colour the plan drew this shape in, kept so the 3D floor can
             * look like the drawing it came from.
             */
            planColor: region.color || null,
            categorised: !!region.categorised,
            /*
             * Category colour once a category has been chosen, because that is
             * the leasing view and the whole point of assigning one. Until then
             * the plan's own colour, which carries the sheet's own meaning: at
             * Yas the purple wing, the tan wing and the blue wing are three
             * different parts of the mall, and rendering all three in the same
             * default grey throws that away for no gain.
             */
            color: (region.categorised && CATEGORY_COLORS[category])
                || region.color
                || CATEGORY_COLORS[category]
                || '#64748B',
            zoneId: 'imported',
            zoneName: region.zoneName || floor.name,
            locationDescription: `${floor.name} — imported plan`,
        };
    });
}

/**
 * Reviewed amenity markers -> the records the router and the 3D scene consume.
 * Untyped markers are dropped: a marker nobody classified cannot be routed to
 * and would show on the map as an anonymous pin.
 */
export function amenitiesToRecords(floor, markers) {
    return markers
        .filter((m) => m.type)
        .map((m, i) => ({
            id: `${floor.id}-am-${i}`,
            type: m.type,
            name: m.name || `${m.type} ${i + 1}`,
            x: m.x,          // normalised 0..1 on the plan sheet
            y: m.y,
            auto: !!m.auto,
        }));
}

/** Attach the same lease/trading/ops model the demo estate uses. */
export function buildImportedEstate(floors) {
    const units = [];
    floors.forEach((floor) => {
        units.push(...(floor.units || []));
    });
    buildCommercials(units);
    return { units, stats: buildMallStats(units) };
}

// ── persistence ─────────────────────────────────────────────────────────────

/**
 * Persist an imported plan. Previews are dropped when they'd overflow the
 * quota — losing the backdrop is survivable, losing the units is not.
 */
export function savePlan(plan) {
    const slim = {
        ...plan,
        floors: plan.floors.map((f) => ({
            ...f,
            previewUrl: (f.previewUrl && f.previewUrl.length < MAX_STORED_PREVIEW_BYTES) ? f.previewUrl : null,
        })),
    };
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
        return { ok: true };
    } catch (err) {
        // Quota exceeded — retry without any previews before giving up.
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                ...slim,
                floors: slim.floors.map((f) => ({ ...f, previewUrl: null })),
            }));
            return { ok: true, droppedPreviews: true };
        } catch {
            return { ok: false, error: 'The plan is too large for browser storage.' };
        }
    }
}

export function loadPlan() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const plan = JSON.parse(raw);
        if (!plan || !Array.isArray(plan.floors) || !plan.floors.length) return null;
        return plan;
    } catch {
        return null;
    }
}

export function clearPlan() {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* nothing to undo */ }
}

export function hasPlan() {
    return !!loadPlan();
}
